import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getChatSessions, deleteChatSession } from '../services/api';

interface ChatSession {
  id: string;
  title: string;
  createdAt: any;
  lastMessageAt: any;
  messageCount: number;
}

interface ChatHistoryProps {
  onSelectSession: (sessionId: string) => void;
  currentSessionId: string | null;
  onNewChat: () => void;
}

// Memoized session item component for performance
const SessionItem = React.memo<{
  session: ChatSession;
  isActive: boolean;
  onSelect: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
}>(({ session, isActive, onSelect, onContextMenu }) => (
  <div
    style={{
      ...styles.sessionItem,
      ...(isActive ? styles.sessionItemActive : {})
    }}
    onClick={onSelect}
    onContextMenu={onContextMenu}
  >
    <div style={styles.sessionTitle}>{session.title}</div>
    <div style={styles.sessionMeta}>
      {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
    </div>
  </div>
));

SessionItem.displayName = 'SessionItem';

const ChatHistory: React.FC<ChatHistoryProps> = ({ onSelectSession, currentSessionId, onNewChat }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; sessionId: string } | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadMoreButtonRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadSessions = useCallback(async (isLoadMore = false) => {
    try {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const result = await getChatSessions(20, isLoadMore ? lastDocId || undefined : undefined);

      if (isLoadMore) {
        setSessions(prev => [...prev, ...result.sessions]);
      } else {
        setSessions(result.sessions);
      }

      setHasMore(result.hasMore);
      setLastDocId(result.lastDocId);
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [lastDocId]);

  // Initial load
  useEffect(() => {
    loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-load until scrollable
  useEffect(() => {
    const checkIfScrollable = async () => {
      if (!scrollContainerRef.current || loading || loadingMore || !hasMore) return;

      const container = scrollContainerRef.current;
      const isScrollable = container.scrollHeight > container.clientHeight;

      // If not scrollable and we have more data, load more automatically
      if (!isScrollable && hasMore && sessions.length > 0) {
        await loadSessions(true);
      }
    };

    // Check after a brief delay to ensure DOM has updated
    const timer = setTimeout(checkIfScrollable, 100);
    return () => clearTimeout(timer);
  }, [sessions, hasMore, loading, loadingMore, loadSessions]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (!loadMoreButtonRef.current || !hasMore) return;

    const options = {
      root: scrollContainerRef.current,
      rootMargin: '100px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) {
        loadSessions(true);
      }
    }, options);

    observerRef.current.observe(loadMoreButtonRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadSessions]);

  const handleContextMenu = (e: React.MouseEvent, sessionId: string) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY, sessionId });
  };

  const handleDeleteSession = async (sessionId: string) => {
    try {
      await deleteChatSession(sessionId);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
      setContextMenu(null);

      // If deleting current session, create a new chat
      if (sessionId === currentSessionId) {
        onNewChat();
      }
    } catch (error) {
      console.error('Failed to delete session:', error);
      alert('Failed to delete chat. Please try again.');
    }
  };

  useEffect(() => {
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const formatDate = (timestamp: any): Date => {
    if (!timestamp) return new Date();
    // Handle Firestore timestamp
    if (timestamp._seconds) {
      return new Date(timestamp._seconds * 1000);
    }
    // Handle regular date
    return new Date(timestamp);
  };

  const getTimeCategory = (date: Date): string => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const lastWeek = new Date(today);
    lastWeek.setDate(lastWeek.getDate() - 7);

    if (date >= today) return 'Today';
    if (date >= yesterday) return 'Yesterday';
    if (date >= lastWeek) return 'Last Week';
    return 'Older';
  };

  const groupSessionsByDate = () => {
    const groups: { [key: string]: ChatSession[] } = {
      'Today': [],
      'Yesterday': [],
      'Last Week': [],
      'Older': []
    };

    sessions.forEach(session => {
      const date = formatDate(session.lastMessageAt);
      const category = getTimeCategory(date);
      groups[category].push(session);
    });

    return groups;
  };

  const groupedSessions = groupSessionsByDate();
  const categoryOrder = ['Today', 'Yesterday', 'Last Week', 'Older'];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button onClick={onNewChat} style={styles.newChatButton}>
        <span style={styles.newChatIcon}>+</span>
        New Chat
      </button>

      <div
        style={styles.sessionsContainer}
        ref={scrollContainerRef}
      >
        {sessions.length === 0 && !loading ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>💬</div>
            <p style={styles.emptyText}>No chat history yet</p>
            <p style={styles.emptySubtext}>Start a new conversation to get started</p>
          </div>
        ) : (
          <>
            {categoryOrder.map((category) => {
              const categorySessions = groupedSessions[category];
              const count = categorySessions.length;
              const isEmpty = count === 0;

              return (
                <div key={category} style={styles.categorySection}>
                  <div style={{
                    ...styles.categoryLabel,
                    ...(isEmpty ? styles.categoryLabelEmpty : {})
                  }}>
                    {category} {count > 0 && `(${count})`}
                  </div>
                  {categorySessions.map(session => (
                    <SessionItem
                      key={session.id}
                      session={session}
                      isActive={session.id === currentSessionId}
                      onSelect={() => onSelectSession(session.id)}
                      onContextMenu={(e) => handleContextMenu(e, session.id)}
                    />
                  ))}
                </div>
              );
            })}

            {/* Intersection Observer sentinel + Load More button */}
            {hasMore && (
              <div ref={loadMoreButtonRef} style={styles.loadMoreSection}>
                {loadingMore ? (
                  <div style={styles.loadingMoreContainer}>
                    <div style={styles.loadingSpinner}></div>
                    <span style={styles.loadingText}>Loading more chats...</span>
                  </div>
                ) : (
                  <button
                    style={styles.loadMoreButton}
                    onClick={() => loadSessions(true)}
                  >
                    Load More Chats
                  </button>
                )}
              </div>
            )}

            {!hasMore && sessions.length > 0 && (
              <div style={styles.endMessage}>
                All chats loaded
              </div>
            )}
          </>
        )}
      </div>

      {contextMenu && (
        <div
          style={{
            ...styles.contextMenu,
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`
          }}
        >
          <button
            style={styles.contextMenuItem}
            onClick={() => handleDeleteSession(contextMenu.sessionId)}
          >
            🗑️ Delete Chat
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    backgroundColor: 'var(--bg-surface-1)',
  },
  newChatButton: {
    margin: 'var(--space-md)',
    padding: '12px 14px',
    backgroundColor: 'var(--accent)',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontSize: 13.5,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    transition: 'all 220ms var(--ease-standard)',
    fontFamily: 'var(--font-sans)',
    boxShadow: 'var(--shadow-sm)',
  },
  newChatIcon: {
    fontSize: '1.25rem',
    fontWeight: 300,
  },
  sessionsContainer: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '0 var(--space-md)',
  },
  categorySection: {
    marginBottom: 'var(--space-lg)',
  },
  categoryLabel: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--fg-3)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: 'var(--space-sm)',
    padding: '0 var(--space-sm)',
  },
  categoryLabelEmpty: {
    opacity: 0.5,
  },
  sessionItem: {
    padding: 'var(--space-md)',
    marginBottom: 'var(--space-xs)',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: 'transparent',
    border: '1px solid transparent',
  },
  sessionItemActive: {
    backgroundColor: 'var(--cg-midnight-100)',
    border: '1px solid var(--border-1)',
  },
  sessionTitle: {
    fontSize: '0.875rem',
    fontWeight: 400,
    color: 'var(--fg-1)',
    marginBottom: '0.25rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
  },
  sessionMeta: {
    fontSize: '0.75rem',
    color: 'var(--fg-3)',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  loadingMoreContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--space-sm)',
    padding: 'var(--space-md)',
  },
  loadingSpinner: {
    width: '24px',
    height: '24px',
    border: '3px solid var(--border-1)',
    borderTop: '3px solid var(--accent)',
    borderRadius: '50%',
    animation: 'cgConicSpin 1s linear infinite',
  },
  loadingText: {
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
  },
  loadMoreSection: {
    padding: 'var(--space-md) 0',
  },
  loadMoreButton: {
    width: '100%',
    padding: 'var(--space-md)',
    backgroundColor: 'transparent',
    color: 'var(--accent)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    fontFamily: 'var(--font-sans)',
    transition: 'all 0.2s ease',
  },
  endMessage: {
    textAlign: 'center' as const,
    padding: 'var(--space-md)',
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: 'var(--space-xl)',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: 'var(--space-md)',
  },
  emptyText: {
    fontSize: '0.9375rem',
    fontWeight: 500,
    color: 'var(--fg-2)',
    margin: '0 0 var(--space-xs) 0',
  },
  emptySubtext: {
    fontSize: '0.8125rem',
    color: 'var(--fg-3)',
    margin: 0,
  },
  contextMenu: {
    position: 'fixed' as const,
    backgroundColor: 'var(--bg-surface-1)',
    border: '1px solid var(--border-1)',
    borderRadius: '8px',
    boxShadow: 'var(--shadow-lg)',
    padding: '0.25rem',
    zIndex: 1000,
  },
  contextMenuItem: {
    display: 'block',
    width: '100%',
    padding: '0.625rem 1rem',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: 'var(--danger)',
    textAlign: 'left' as const,
    transition: 'background-color 0.2s ease',
    fontFamily: 'var(--font-sans)',
    whiteSpace: 'nowrap' as const,
  },
};

export default ChatHistory;
